/**
 * P4: canonical appointment-actor eligibility.
 *
 * Actor eligibility derives from the user's ACTIVE membership in the SELECTED
 * Account and from nothing else. The global `users.role` has not been an
 * Account-role authority source since WP-1, and `/team/users` previously
 * projected it in place of the membership role -- so these surfaces selected
 * actors from whichever role happened to sit on the principal document.
 *
 * Both appointment consumers import these predicates rather than re-declaring
 * the rule, so the two cannot drift apart again.
 *
 * The two selectors are NOT disjoint: Booking Agent is a superset that adds
 * Sales-Agent-eligible users to the dedicated `booking_agent` role. See
 * `isBookingAgentEligible` for why that widens CHOICE without widening
 * ATTRIBUTION.
 *
 * Vocabulary is the frozen six Account roles. "agent" is the pre-P4 name for
 * `sales_agent` and is deliberately NOT accepted; "super_admin" is platform
 * authority and is never an Account role.
 */

/** Roles that may be listed as a Sales Agent via the membership persona flag. */
export const SALES_AGENT_FLAG_ROLES = ["owner", "admin"];

/**
 * Sales Agent: `sales_agent` intrinsically, or owner/admin who have been
 * explicitly listed via the membership-level `list_as_sales_agent` flag.
 */
export function isSalesAgentEligible(u) {
  const role = u?.membership_role;
  if (role === "sales_agent") return true;
  return SALES_AGENT_FLAG_ROLES.includes(role) && Boolean(u?.list_as_sales_agent);
}

/**
 * Booking Agent: the `booking_agent` membership role, OR Sales-Agent
 * eligibility in the same Account.
 *
 * ELIGIBILITY IS NOT ATTRIBUTION. This distinction is the whole point of the
 * rule, so it is worth stating plainly:
 *
 *   ELIGIBILITY  -- who a person may CHOOSE from. A Sales-Agent-eligible user
 *                   may legitimately be the person who prospected and
 *                   scheduled the Appointment; a Junior Agent is carried as a
 *                   Sales Agent persona but also books. Offering them here is
 *                   a widened CHOICE.
 *
 *   ATTRIBUTION  -- what the system RECORDS. `sales_agent_user_id` is the
 *                   sales/closing actor; `booking_agent_user_id` is whoever
 *                   actually booked. They remain independent fields, and
 *                   nothing here permits inferring one from the other.
 *
 * So this union must NEVER be read as licence to auto-fill. Being selectable
 * as a Booking Agent does not make someone the booking actor: an omitted
 * Booking Agent stays omitted, and no code path in this module copies
 * `sales_agent_user_id` into `booking_agent_user_id`. Collapsing Activity
 * Agent / Booking Agent / Originator into one identity is what the earlier
 * current-user fallbacks did, and those stay removed.
 *
 * The Sales arm COMPOSES `isSalesAgentEligible` rather than restating it, so
 * the two rules cannot drift: `owner`/`admin` still need the membership-level
 * `list_as_sales_agent` flag to reach either selector, and a dedicated
 * `booking_agent` gains no Sales eligibility from this.
 *
 * An Account with neither a `booking_agent` membership nor any Sales-eligible
 * member still yields a legitimately empty set.
 */
export function isBookingAgentEligible(u) {
  return u?.membership_role === "booking_agent" || isSalesAgentEligible(u);
}

/** Options for the Sales Agent selector, from a /team/users item list. */
export function salesAgentOptionsFrom(teamUsers) {
  return (teamUsers || []).filter(isSalesAgentEligible);
}

/**
 * Options for the Booking Agent selector, from a /team/users item list.
 *
 * ONE pass over ONE list, evaluating the predicate once per user -- which is
 * what makes de-duplication intrinsic. A user who qualifies through BOTH arms
 * of the union is still a single array element. Building this as two filtered
 * lists concatenated would reintroduce duplicates and then need a `Set` to
 * remove them again, so the single pass is deliberate, not incidental.
 */
export function bookingAgentOptionsFrom(teamUsers) {
  return (teamUsers || []).filter(isBookingAgentEligible);
}
